import React from 'react';
import { createColumnHelper, type Row, type RowData, type Table } from '@tanstack/react-table';

import { useTranslation } from 'src/i18n';
import { Checkbox } from 'src/component/ds/ui/checkbox';
import { type DataTableFeatures } from 'src/component/ds/ui/dataTableFeatures';

function SelectAllCheckbox<TData extends RowData> ({ table }: { table: Table<DataTableFeatures, TData> }) {
  const { t } = useTranslation();
  return (
    <Checkbox
      checked={table.getIsAllRowsSelected() || (table.getIsSomeRowsSelected() ? 'indeterminate' : false)}
      onCheckedChange={(value) => table.toggleAllRowsSelected(!!value)}
      aria-label={t('common.selectAll')}
    />
  );
}

function SelectRowCheckbox<TData extends RowData> ({ row }: { row: Row<DataTableFeatures, TData> }) {
  const { t } = useTranslation();
  if (!row.getCanSelect()) return null;
  return (
    <Checkbox
      checked={row.getIsSelected()}
      onCheckedChange={(value) => row.toggleSelected(!!value, { selectChildren: false })}
      aria-label={t('table.selectRow')}
    />
  );
}

/*
 * The shadcn select column. Parent and child rows select independently;
 * the header still selects every row. Rows that can't be selected
 * (`enableRowSelection`) get no checkbox.
 */
export function dsSelectColumn<TData extends RowData> () {
  const helper = createColumnHelper<DataTableFeatures, TData>();
  return helper.display({
    id: 'select',
    meta: { interactive: true, className: 'ds-w-8' },
    header: ({ table }) => <SelectAllCheckbox table={table} />,
    cell: ({ row }) => <SelectRowCheckbox row={row} />,
  });
}
